import { AIToolCall } from "@coeff/api";
import { Checkbox, Collapse, Empty, Space, Spin, Tag, Typography, message } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styled from "styled-components";

import { useAgentThreadMessages } from "../../../api";

import { resolveAnchor, userTurnsByNumber } from "./userTurns";

const { Text } = Typography;

const MessageCard = styled.div<{ $highlighted?: boolean }>`
  background: ${p => (p.$highlighted ? "#fffbe6" : "#fff")};
  border: 1px solid ${p => (p.$highlighted ? "#faad14" : "#e8e8e8")};
  border-radius: 6px;
  padding: 10px 12px;
  transition: background 400ms ease, border-color 400ms ease;
  scroll-margin: 12px;

  & + & {
    margin-top: 8px;
  }
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;

const MessageText = styled.div`
  color: #333;
  font-size: 13px;
  line-height: 1.55;
  word-break: break-word;

  /* ReactMarkdown emits real block elements; keep them tight enough to scan in a transcript. */
  > *:first-child {
    margin-top: 0;
  }
  > *:last-child {
    margin-bottom: 0;
  }
  p,
  ul,
  ol,
  table {
    margin: 0 0 8px;
  }
  ul,
  ol {
    padding-left: 20px;
  }
  li {
    margin-bottom: 2px;
  }
  code {
    background: #f0f2f5;
    border-radius: 3px;
    padding: 1px 4px;
    font-size: 12px;
  }
  pre {
    background: #f6f8fa;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    padding: 8px 10px;
    overflow-x: auto;
  }
  pre code {
    background: none;
    padding: 0;
  }
  table {
    border-collapse: collapse;
    display: block;
    overflow-x: auto;
  }
  th,
  td {
    border: 1px solid #e8e8e8;
    padding: 3px 8px;
    text-align: left;
  }
  h1,
  h2,
  h3,
  h4 {
    font-size: 13px;
    font-weight: 600;
    margin: 10px 0 4px;
  }
`;

const MoreButton = styled.button`
  background: none;
  border: 0;
  padding: 2px 0;
  margin-top: 2px;
  font: inherit;
  font-size: 12px;
  color: #1890ff;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const CodeBlock = styled.pre`
  margin: 8px 0 0;
  padding: 8px 10px;
  background: #f6f8fa;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  color: #333;
  font-size: 12px;
  max-height: 260px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Screenshot = styled.img`
  display: block;
  max-width: 520px;
  max-height: 260px;
  margin-top: 8px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
`;

const ROLE_COLOR: Record<string, string> = {
  user: "blue",
  agent: "purple",
  event: "gold",
};

const ROLE_LABEL: Record<string, string> = {
  user: "USER",
  agent: "AGENT",
  event: "EVENT",
};

function formatDateTime(iso?: string): string {
  if (!iso) {
    return "-";
  }
  return new Date(iso).toLocaleString();
}

function formatToolArguments(toolCall: AIToolCall): string {
  const args = toolCall.function?.arguments;
  if (!args) {
    return "";
  }
  try {
    return JSON.stringify(JSON.parse(args), null, 2);
  } catch {
    return args;
  }
}

// Long messages start clamped so a 300-message thread stays scannable. Anything shorter renders
// whole — a collapse whose header already shows the text just prints it twice.
const CLAMP_CHARS = 600;

const MessageBody: React.FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const clampable = text.length > CLAMP_CHARS;
  return (
    <>
      <MessageText>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {clampable && !expanded ? `${text.slice(0, CLAMP_CHARS).trimEnd()}…` : text}
        </ReactMarkdown>
      </MessageText>
      {clampable && (
        <MoreButton type="button" onClick={() => setExpanded(v => !v)}>
          {expanded ? "Show less" : `Show more (${text.length.toLocaleString()} chars)`}
        </MoreButton>
      )}
    </>
  );
};

interface ThreadTranscriptProps {
  threadId: string;
  /**
   * A finding's anchor to scroll to and highlight — its [U<n>] turn, with the user's quote as a
   * fallback when the turn does not resolve. `nonce` changes on every request so clicking the
   * same finding twice re-scrolls; without it the effect would not re-run.
   */
  focusRequest?: { turn?: number; quote?: string; nonce: number };
}

export const ThreadTranscript: React.FC<ThreadTranscriptProps> = ({ threadId, focusRequest }) => {
  const [hideEvents, setHideEvents] = useState(false);
  const [hideToolCalls, setHideToolCalls] = useState(false);
  const [userAndAssistantOnly, setUserAndAssistantOnly] = useState(false);
  const [highlightedId, setHighlightedId] = useState<string | undefined>();
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const messagesQuery = useAgentThreadMessages(threadId);
  const messages = messagesQuery.data ?? [];
  const loading = messagesQuery.isLoading;

  useEffect(() => {
    if (messagesQuery.isError) {
      message.error("Failed to load thread messages");
    }
  }, [messagesQuery.isError]);

  const userTurns = useMemo(() => userTurnsByNumber(messages), [messages]);

  useEffect(() => {
    if (!focusRequest) {
      return;
    }
    const targetId = resolveAnchor(userTurns, focusRequest);
    // Neither the turn nor the quote landed: leave the view where it is rather than scrolling
    // somewhere misleading.
    if (!targetId) {
      return;
    }
    setHighlightedId(targetId);
    // The node only exists once the message list has painted.
    const raf = window.requestAnimationFrame(() => {
      cardRefs.current[targetId]?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    const clear = window.setTimeout(() => setHighlightedId(undefined), 2600);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(clear);
    };
  }, [focusRequest, userTurns]);

  const visibleMessages = useMemo(() => {
    return messages.filter(msg => {
      if (userAndAssistantOnly && msg.role !== "user" && msg.role !== "agent") {
        return false;
      }
      if (hideEvents && msg.role === "event") {
        return false;
      }
      return true;
    });
  }, [messages, hideEvents, userAndAssistantOnly]);

  if (loading) {
    return <Spin />;
  }

  return (
    <div>
      <Space size="middle" style={{ marginBottom: 10 }} wrap>
        <Text strong style={{ fontSize: 13 }}>
          Transcript ({messages.length})
        </Text>
        <Checkbox checked={hideToolCalls} onChange={e => setHideToolCalls(e.target.checked)}>
          Hide tool calls
        </Checkbox>
        <Checkbox checked={hideEvents} onChange={e => setHideEvents(e.target.checked)}>
          Hide events
        </Checkbox>
        <Checkbox
          checked={userAndAssistantOnly}
          onChange={e => setUserAndAssistantOnly(e.target.checked)}
        >
          Only user + assistant
        </Checkbox>
      </Space>

      {visibleMessages.length === 0 ? (
        <Empty
          description="No messages match the current filters"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        visibleMessages.map(msg => {
          const text = msg.content?.text || "";
          const reasoning = msg.content?.reasoning;
          const toolCalls = msg.content?.tool_calls || [];
          const role = msg.role || "agent";
          const summaryLabel = msg.content?.tool_call_summary_label;
          return (
            <MessageCard
              key={msg.id}
              ref={node => {
                if (msg.id) {
                  cardRefs.current[msg.id] = node;
                }
              }}
              $highlighted={!!msg.id && msg.id === highlightedId}
            >
              <MessageHeader>
                <Space>
                  <Tag color={ROLE_COLOR[role] || "default"}>{ROLE_LABEL[role] || role}</Tag>
                  {msg.content?.event_type && <Tag color="gold">{msg.content.event_type}</Tag>}
                  {summaryLabel && !text && <Tag color="cyan">{summaryLabel}</Tag>}
                  {toolCalls.length > 0 && !hideToolCalls && (
                    <Tag color="default">
                      {toolCalls.length} tool call{toolCalls.length === 1 ? "" : "s"}
                    </Tag>
                  )}
                </Space>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {formatDateTime(msg.created_dt)}
                </Text>
              </MessageHeader>

              {text && <MessageBody text={text} />}

              {reasoning && (
                <Collapse ghost defaultActiveKey={[]}>
                  <Collapse.Panel header="Reasoning" key="reasoning">
                    {/* Already behind a collapse, so render it whole rather than clamping twice. */}
                    <MessageText>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{reasoning}</ReactMarkdown>
                    </MessageText>
                  </Collapse.Panel>
                </Collapse>
              )}

              {!hideToolCalls && toolCalls.length > 0 && (
                <Collapse ghost defaultActiveKey={[]}>
                  <Collapse.Panel header="Tool Calls" key="tools">
                    {toolCalls.map(toolCall => (
                      <div key={toolCall.id} style={{ marginBottom: 10 }}>
                        <Text strong style={{ fontSize: 12 }}>
                          {toolCall.function?.name || "tool"}
                        </Text>
                        <CodeBlock>{formatToolArguments(toolCall) || "(no arguments)"}</CodeBlock>
                      </div>
                    ))}
                  </Collapse.Panel>
                </Collapse>
              )}

              {msg.content?.screenshot && (
                <Screenshot src={msg.content.screenshot} alt="Thread screenshot" />
              )}
            </MessageCard>
          );
        })
      )}
    </div>
  );
};
